import type { ReactNode } from 'react'
import { Suspense } from 'react'
import { ProtectedRoute } from '@/components/auth/ProtectedRoute'
import { MainLayout } from '@/components/layout/MainLayout'
import { TauriTitleBarShell } from '@/components/layout/TauriTitleBarShell'
import { ModeToggle } from '@/components/mode-toggle'
import { Button } from '@/components/ui/button'
import { Loading } from '@/components/ui/loading'
import { Skeleton } from '@/components/ui/skeleton'
import { TooltipProvider } from '@/components/ui/tooltip'
import { isTauri } from '@/lib/utils/tauri'
import { handleLogout } from '@/utils/auth/auth-utils'
import { SignOutIcon } from '@phosphor-icons/react'
import { Outlet } from 'react-router-dom'

// Protected Layout component that applies MainLayout to all protected routes
export function ProtectedLayout() {
  return (
    <ProtectedRoute>
      <MainLayout>
        <div className="h-full">
          <Outlet />
        </div>
      </MainLayout>
    </ProtectedRoute>
  )
}

export function AuthRouteLayout() {
  return (
    <TauriTitleBarShell className="min-h-screen w-full">
      <Outlet />
    </TauriTitleBarShell>
  )
}

// Client portal route shell without org navigation
export function ClientPortalRouteShell() {
  return (
    <ProtectedRoute>
      <TooltipProvider delayDuration={0}>
        <Outlet />
      </TooltipProvider>
    </ProtectedRoute>
  )
}

// Simple Layout for setup flows without sidebar - with beautiful animated background
export function SetupLayout() {
  const isDesktopApp = isTauri()

  return (
    <ProtectedRoute>
      <TauriTitleBarShell>
        <div className="relative h-[var(--tauri-content-height,100vh)] w-full overflow-hidden bg-background">
          {/* Dark Mode Toggle - positioned in top-right, z-50 to be above TauriDragRegion */}
          {/* Only show for web since Tauri has it in the title bar */}
          {!isDesktopApp && (
            <div className="absolute right-4 top-4 z-50">
              <ModeToggle />
            </div>
          )}

          {/* Modern gradient background using theme colors with brand accents */}
          <div className="from-primary/12 via-muted/8 via-accent/6 to-primary/18 animate-gradient-slow dark:via-accent/12 absolute inset-0 bg-gradient-to-br dark:from-primary/20 dark:via-muted/15 dark:to-primary/25"></div>

          {/* Subtle brand color accent layers */}
          <div className="from-[hsl(var(--brand-blue))]/8 via-[hsl(var(--brand-green))]/6 to-[hsl(var(--brand-orange))]/8 animate-pulse-slow dark:from-[hsl(var(--brand-blue))]/12 dark:via-[hsl(var(--brand-green))]/8 dark:to-[hsl(var(--brand-orange))]/12 absolute inset-0 bg-gradient-to-tr via-transparent dark:via-transparent"></div>
          <div className="from-[hsl(var(--brand-red))]/6 via-[hsl(var(--brand-yellow))]/8 animate-gradient-slow animation-delay-3000 dark:via-[hsl(var(--brand-yellow))]/12 absolute inset-0 bg-gradient-to-tl to-transparent dark:from-[hsl(var(--brand-red))]/10 dark:to-transparent"></div>

          {/* Modern overlay for clean contrast */}
          <div className="absolute inset-0 bg-background/60 dark:bg-background/70"></div>

          {/* Modern subtle flowing shapes using theme and brand colors */}
          <div className="absolute left-0 top-0 h-full w-full overflow-hidden opacity-25 dark:opacity-20">
            {/* Large organic shapes with modern opacity */}
            <div className="to-muted/12 animate-blob animation-delay-1000 animate-float-gentle dark:to-muted/18 absolute left-[10%] top-[10%] h-96 w-96 rounded-full bg-gradient-to-br from-primary/20 mix-blend-overlay dark:from-primary/25"></div>
            <div className="from-[hsl(var(--brand-orange))]/18 to-[hsl(var(--brand-yellow))]/12 animate-blob animation-delay-3000 animate-float-reverse dark:from-[hsl(var(--brand-orange))]/22 dark:to-[hsl(var(--brand-yellow))]/16 absolute bottom-[15%] right-[15%] h-80 w-80 rounded-full bg-gradient-to-tr mix-blend-overlay"></div>
            <div className="from-[hsl(var(--brand-green))]/18 to-accent/12 animate-blob animation-delay-5000 animate-float-slow dark:from-[hsl(var(--brand-green))]/22 dark:to-accent/16 absolute left-[20%] top-[50%] h-72 w-72 rounded-full bg-gradient-to-bl mix-blend-overlay"></div>
            <div className="from-[hsl(var(--brand-red))]/18 to-destructive/12 animate-blob animation-delay-2000 animate-drift-horizontal dark:from-[hsl(var(--brand-red))]/22 dark:to-destructive/16 absolute right-[5%] top-[20%] h-64 w-64 rounded-full bg-gradient-to-br mix-blend-overlay"></div>
            <div className="from-[hsl(var(--brand-blue))]/18 to-primary/12 animate-blob animation-delay-4000 animate-drift-vertical dark:from-[hsl(var(--brand-blue))]/22 dark:to-primary/16 absolute bottom-[40%] left-[60%] h-56 w-56 rounded-full bg-gradient-to-tl mix-blend-overlay"></div>

            {/* Medium abstract shapes with refined opacity */}
            <div className="animate-blob animation-delay-6000 animate-float-gentle absolute right-[25%] top-[70%] h-48 w-48 rounded-full bg-gradient-to-r from-secondary/15 to-muted/10 mix-blend-overlay dark:from-secondary/20 dark:to-muted/15"></div>
            <div className="animate-blob animation-delay-7000 animate-float-reverse absolute left-[70%] top-[35%] h-40 w-40 rounded-full bg-gradient-to-l from-accent/15 to-primary/10 mix-blend-overlay dark:from-accent/20 dark:to-primary/15"></div>

            {/* Subtle grid pattern */}
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGZpbGw9IiNmZmZmZmYwMyIgZD0iTTAgMGg2MHY2MEgweiIvPjxwYXRoIGQ9Ik0zNiAxOGgtMnYyNGgyVjE4em0tMTAgMGgydjI0aC0yVjE4ek0xOCAzNnYtMmgyNHYyaC0yNHptMC0xMGgyNHYyaC0yNHYtMnoiIGZpbGw9IiNmZmZmZmYwOCIvPjwvZz48L3N2Zz4=')] opacity-10 dark:opacity-15"></div>
          </div>

          {/* Modern subtle floating particles using theme colors */}
          <div className="absolute inset-0 overflow-hidden opacity-60 dark:opacity-50">
            <div className="particle bg-primary/12 dark:bg-primary/18 left-[45%] top-[15%]"></div>
            <div className="particle bg-[hsl(var(--brand-orange))]/12 animation-delay-2000 dark:bg-[hsl(var(--brand-orange))]/18 left-[20%] top-[35%]"></div>
            <div className="particle bg-[hsl(var(--brand-green))]/12 animation-delay-4000 dark:bg-[hsl(var(--brand-green))]/18 left-[60%] top-[70%]"></div>
            <div className="particle bg-[hsl(var(--brand-red))]/12 animation-delay-1000 dark:bg-[hsl(var(--brand-red))]/18 left-[80%] top-[20%]"></div>
            <div className="particle bg-accent/12 animation-delay-3000 dark:bg-accent/18 left-[10%] top-[60%]"></div>
            <div className="particle bg-[hsl(var(--brand-yellow))]/12 animation-delay-2500 dark:bg-[hsl(var(--brand-yellow))]/18 left-[25%] top-[85%]"></div>
            <div className="particle bg-secondary/12 animation-delay-1500 dark:bg-secondary/18 left-[70%] top-[40%]"></div>
            <div className="particle bg-[hsl(var(--brand-blue))]/12 animation-delay-3500 dark:bg-[hsl(var(--brand-blue))]/18 left-[30%] top-[15%]"></div>
            <div className="particle animation-delay-5000 left-[85%] top-[50%] bg-primary/10 dark:bg-primary/15"></div>
            <div className="particle animation-delay-6000 left-[5%] top-[75%] bg-[hsl(var(--brand-orange))]/10 dark:bg-[hsl(var(--brand-orange))]/15"></div>
          </div>

          {/* Main Content - relative positioned above the backgrounds, scrolls independently so the background stays fixed */}
          <main className="relative z-10 h-full w-full overflow-y-auto">
            <Outlet />
          </main>

          {/* Sign Out Button - positioned absolutely at bottom right */}
          {/* Only show for web since Tauri has it in the user menu */}
          {!isDesktopApp && (
            <div className="fixed bottom-6 right-6 z-50 max-md:bottom-4 max-md:right-4">
              <Button
                variant="outline"
                onClick={handleLogout}
                className="rounded-full border-border bg-card px-6 text-sm text-muted-foreground shadow-lg transition-all duration-200 hover:border-border/70 hover:bg-card hover:text-foreground max-md:px-4"
              >
                <SignOutIcon className="mr-2 h-4 w-4" />
                Sign out
              </Button>
            </div>
          )}
        </div>
      </TauriTitleBarShell>
    </ProtectedRoute>
  )
}

// Global loading component - centralized to ensure consistent loading UI
export const AppLoader = ({
  inline = false,
  size = 'lg',
}: {
  inline?: boolean
  size?: 'sm' | 'md' | 'lg'
}) => {
  if (inline) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loading variant="spinner" size={size} />
      </div>
    )
  }

  // For full-screen loading, use isInitialLoad flag with logo variant
  return <Loading variant="logo" size={size} isInitialLoad={true} />
}

// Suspense wrapper with app loader
interface LazyLoadProps {
  children: ReactNode
}

/** Route loading stays inside its shell; only app boot uses the logo overlay. */
export function RouteLoader() {
  return (
    <div
      role="status"
      aria-label="Loading page"
      className="h-full min-h-0 w-full bg-background p-6"
    >
      <span className="sr-only">Loading page</span>
      <div aria-hidden="true" className="mx-auto max-w-4xl space-y-6">
        <Skeleton className="h-7 w-48" shimmer={false} />
        <div className="space-y-3">
          {[0, 1, 2, 3].map(row => (
            <Skeleton
              key={row}
              className="h-12 w-full"
              intensity="subtle"
              shimmer={false}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

export function LazyLoad({ children }: LazyLoadProps) {
  return <Suspense fallback={<RouteLoader />}>{children}</Suspense>
}
